/**
 * 同時視聴の WebSocket 接続（要件定義 17 章）。
 *
 * 視聴者数とコメントを 1 本の接続で受け取る。
 * 回線が切れても放送そのものは止めたくないので、
 * 接続の失敗はサイト全体のエラーにせず、黙って再接続を試みる。
 */

import {
  emptyReactionCounts,
  type LiveClientMessage,
  type LiveComment,
  type LiveRejectReason,
  type LiveServerMessage,
  REACTION_MIN_INTERVAL_MS,
  type ReactionCounts,
  type ReactionKind,
  type RecordedReaction,
  replaySchedule,
} from '@shared/live.ts'
import { createEffect, createSignal, onCleanup, onMount } from 'solid-js'

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000

/** 弾かれた理由を表示しておく時間 */
const REJECTION_VISIBLE_MS = 4000

/** リアクションの粒が飛んでいる時間。CSS のアニメーションと合わせる */
const BURST_VISIBLE_MS = 2400

/**
 * 同時に飛ばす粒の上限。
 * 人が増えるほどリアクションは重なるので、画面が埋まらないところで頭打ちにする。
 */
const BURST_MAX = 24

/**
 * 流し直しの粒が、予定より遅れて出るのを許す幅。
 *
 * タブが裏に回ると setTimeout は止められ、戻ったときにまとめて発火する。
 * そのまま流すと、離れていた間のぶんが一度に降ってくる。
 */
const REPLAY_LATE_TOLERANCE_MS = 3000

/** 飛んでいるリアクションの粒 1 つ */
export type ReactionBurst = {
  id: number
  kind: ReactionKind
  /**
   * 過去の放送で押されたものを流し直しているか。
   * いま誰かが押したものと見分けが付くよう、画面では薄く出す。
   */
  replay?: boolean
}

/**
 * 接続時点の番組を URL に載せる。
 * サーバーは番組表を持たず、これを合図に前の番組のコメントを一掃する。
 */
const liveUrl = (programStartAt: string | undefined, videoId: string | undefined): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const url = new URL(`${protocol}//${window.location.host}/api/live`)
  if (programStartAt) url.searchParams.set('program', programStartAt)
  if (videoId) url.searchParams.set('video', videoId)
  return url.toString()
}

export type LiveStore = ReturnType<typeof createLiveStore>

/**
 * @param programStartAt いま放送中の番組の開始時刻。番組の切り替わりをサーバーに知らせるのに使う
 */
export const createLiveStore = (
  programStartAt: () => string | undefined,
  videoId: () => string | undefined,
) => {
  const [viewers, setViewers] = createSignal(0)
  const [comments, setComments] = createSignal<LiveComment[]>([])
  const [connected, setConnected] = createSignal(false)
  /** サーバーから割り当てられた自分の表示名 */
  const [name, setName] = createSignal('')
  const [rejection, setRejection] = createSignal<LiveRejectReason | null>(null)
  /** 運営がコメントを止めているか */
  const [muted, setMuted] = createSignal(false)
  /** いまの番組に集まったリアクション数 */
  const [reactions, setReactions] = createSignal<ReactionCounts>(emptyReactionCounts())
  /** いま飛んでいるリアクションの粒 */
  const [bursts, setBursts] = createSignal<ReactionBurst[]>([])

  let rejectionTimer: ReturnType<typeof setTimeout> | undefined
  let burstSeq = 0
  const burstTimers = new Set<ReturnType<typeof setTimeout>>()
  /** 自分が最後にリアクションした時刻。サーバーに弾かれる連打を送らないために持つ */
  let lastReactionAt = 0

  /** 粒を 1 つ飛ばし、飛び終わったら消す */
  const addBurst = (kind: ReactionKind, replay = false): void => {
    burstSeq += 1
    const id = burstSeq
    setBursts((current) => [...current, { id, kind, replay }].slice(-BURST_MAX))

    const timer = setTimeout(() => {
      burstTimers.delete(timer)
      setBursts((current) => current.filter((burst) => burst.id !== id))
    }, BURST_VISIBLE_MS)
    burstTimers.add(timer)
  }

  const replayTimers = new Set<ReturnType<typeof setTimeout>>()

  const clearReplay = (): void => {
    for (const timer of replayTimers) clearTimeout(timer)
    replayTimers.clear()
  }

  /** サーバーから渡された、いまの動画に記録されているリアクション */
  const [recorded, setRecorded] = createSignal<RecordedReaction[]>([])

  /**
   * 過去の放送で押されたリアクションを、同じところで流し直す。
   *
   * サーバーはいつ流すかを決めない。全員が同じ再生位置を見ているので、
   * 手元で時刻から決めても見え方はそろうし、粒ごとにサーバーを起こさずに済む。
   *
   * 記録と番組のどちらが先に分かるかは決まっていない（接続の方が
   * 放送情報の取得より早いことがある）ので、両方を見て組み直す。
   */
  createEffect(() => {
    const list = recorded()
    const start = programStartAt()
    clearReplay()

    const startMs = start ? Date.parse(start) : Number.NaN
    if (list.length === 0 || !Number.isFinite(startMs)) return

    for (const item of replaySchedule(list, Date.now() - startMs)) {
      const dueAt = Date.now() + item.delayMs
      const timer = setTimeout(() => {
        replayTimers.delete(timer)
        // タブが止まっていた間に溜まったぶんは、まとめて流さず捨てる
        if (Date.now() - dueAt > REPLAY_LATE_TOLERANCE_MS) return
        addBurst(item.kind, true)
      }, item.delayMs)
      replayTimers.add(timer)
    }
  })

  let socket: WebSocket | undefined
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let disposed = false
  /** サーバーに知らせ終えた番組。同じ番組を何度も送らないために持つ */
  let sentProgram: string | undefined

  const handle = (message: LiveServerMessage): void => {
    switch (message.type) {
      case 'init':
        setViewers(message.viewers)
        setComments(message.comments)
        setReactions(message.reactions)
        setName(message.name)
        setMuted(message.muted)
        setRecorded(message.replay)
        break
      case 'replay':
        setRecorded(message.reactions)
        break
      case 'viewers':
        setViewers(message.count)
        break
      case 'comment':
        setComments((current) => [...current, message.comment])
        break
      case 'reaction':
        setReactions((current) => ({ ...current, [message.kind]: message.count }))
        addBurst(message.kind)
        break
      case 'clear':
        setComments([])
        setReactions(emptyReactionCounts())
        // 次の動画ぶんは replay で改めて配られる
        setRecorded([])
        break
      case 'muted':
        setMuted(message.muted)
        break
      case 'rejected':
        setRejection(message.reason)
        clearTimeout(rejectionTimer)
        rejectionTimer = setTimeout(() => setRejection(null), REJECTION_VISIBLE_MS)
        break
    }
  }

  const scheduleReconnect = (): void => {
    if (disposed || reconnectTimer) return
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
    attempt += 1
    reconnectTimer = setTimeout(() => {
      reconnectTimer = undefined
      connect()
    }, delay)
  }

  const connect = (): void => {
    if (disposed || socket) return

    const program = programStartAt()
    const ws = new WebSocket(liveUrl(program, videoId()))
    socket = ws
    sentProgram = program

    ws.addEventListener('open', () => {
      attempt = 0
      setConnected(true)
    })

    ws.addEventListener('message', (event) => {
      try {
        handle(JSON.parse(String(event.data)) as LiveServerMessage)
      } catch {
        // 壊れたメッセージは黙って捨てる
      }
    })

    const dropped = (): void => {
      if (socket !== ws) return
      socket = undefined
      setConnected(false)
      setViewers(0)
      scheduleReconnect()
    }

    ws.addEventListener('close', dropped)
    ws.addEventListener('error', dropped)
  }

  const send = (message: LiveClientMessage): boolean => {
    if (socket?.readyState !== WebSocket.OPEN) return false
    socket.send(JSON.stringify(message))
    return true
  }

  const postComment = (text: string): boolean => {
    setRejection(null)
    return send({ type: 'comment', text })
  }

  /**
   * リアクションを送る。
   *
   * 粒は自分ぶんも含めてサーバーから配信されたものだけを飛ばす。
   * 手元で先に飛ばすと、届かなかったときに自分にだけ見えることになる。
   */
  const sendReaction = (kind: ReactionKind): boolean => {
    const now = Date.now()
    if (now - lastReactionAt < REACTION_MIN_INTERVAL_MS) return false
    if (!send({ type: 'reaction', kind })) return false
    lastReactionAt = now
    return true
  }

  // 番組が切り替わったらサーバーに知らせる。これを合図にコメントが一掃される
  createEffect(() => {
    const program = programStartAt()
    if (!program || program === sentProgram) return
    // 切れているときは送れないが、繋ぎ直すときに URL で知らせるので取りこぼさない
    if (send({ type: 'program', programStartAt: program, videoId: videoId() })) {
      sentProgram = program
    }
  })

  onMount(() => {
    connect()

    // スリープやタブ復帰で切れていたら繋ぎ直す
    const resume = (): void => {
      if (document.visibilityState === 'visible' && !socket) {
        clearTimeout(reconnectTimer)
        reconnectTimer = undefined
        attempt = 0
        connect()
      }
    }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('online', resume)

    onCleanup(() => {
      disposed = true
      clearTimeout(rejectionTimer)
      clearTimeout(reconnectTimer)
      for (const timer of burstTimers) clearTimeout(timer)
      burstTimers.clear()
      clearReplay()
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('online', resume)
      socket?.close()
      socket = undefined
    })
  })

  return {
    viewers,
    comments,
    connected,
    name,
    rejection,
    muted,
    reactions,
    bursts,
    postComment,
    sendReaction,
  }
}
